// The windows of the catalogue services in « Connecteurs du bureau » and Administration →
// Connecteurs: « Ajouter <service> » (a name — the catalogue's, then « Canva 2 »… if taken — and
// the service's own fields) and the settings of a plugin the office serves itself (Mailgun: its
// private key, region and default domain; GitHub: its token; Browserbase: its API key; Shopify: its store, « Se connecter avec Shopify » or a
// pasted Admin API token). A remote service (Canva) is added with its name only; its connect
// window opens next (the caller's `onAdded`). « Se connecter avec Shopify » asks the server for
// the store's authorize address (POST …/shopify/start: a state bound to this session) and sends
// the browser there; Shopify brings it back to #/connectors/<id>?shopify=… (myconnectors.js says
// how it went). Once the office's Shopify app is set, the store may be left empty: the server then
// gives one of Shopify's install pages, where the person logs in and picks the store, and Shopify
// comes back through the office's launch address. A store picked in Shopify with no connector
// chosen yet (its « Install app », the app opened from the Shopify admin) lands on
// #/connectors?shopify=launch&l=<id>: openShopifyLaunch asks which connector receives it. Without
// the owner's app, when the server has the Shopify CLI (apps.shopify.cli), « Se connecter avec
// Shopify » adds the store as a CLI one (`auth: 'cli'`, the store required) and its connect window
// opens (the CLI's login: the store's page, then the pasted address; the caller's `onAdded` /
// `onLogin`). With neither, the token is the way (an administrator gets a shortcut to the app's
// settings). A key or token typed here goes to the server once and leaves its field at once: never
// stored, logged or shown again (the server shows its last 4 characters). A Google Drive has its
// own windows (myconnectors/drive.js: a name to add it, like a store, then its connect window — the
// Google account, its folder, the office's files): openCatalogAdd and openPluginSettings hand it over.
import { h, button, input, field, select, uid } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { errorText } from '../admin/model.js';
import { alertBox, formError, setBusy } from '../admin/common.js';
import { openConnectWindow } from './connect.js';
import { openDriveAdd, openDriveSettings } from './drive.js';
import { GDRIVE_ID } from './drive-model.js';
import {
  LIBRARY_BASE, LABEL_MAX, MAILGUN_REGIONS, connectorOf, normalizeLibrary, safeShopifyUrl, shopifyLaunchOf, shopifyRoute, suggestLabel, validatePluginForm,
} from './model.js';
import './strings.js';

export { shopifyRoute };

/** An add or a check with the provider (Mailgun, Shopify; Claude Code for a remote one) answers within this. */
export const PLUGIN_TIMEOUT_MS = 60_000;
/** « Se connecter avec Shopify » asks the server for the store's address (no provider call). */
const START_TIMEOUT_MS = 20_000;
const enc = encodeURIComponent;
const FIELDS = ['label', 'apiKey', 'accessKey', 'secretKey', 'region', 'domain', 'shop', 'accessToken'];

/** Inputs kept away from password managers' autofill (a key typed once, not a login). */
const NO_AUTOFILL = { autocomplete: 'off', autocapitalize: 'none', spellcheck: false, 'data-1p-ignore': '', 'data-lpignore': 'true', 'data-bwignore': '' };

/** The browser goes to Shopify (tests pass their own). */
function defaultNavigate(url) {
  globalThis.location?.assign?.(url);
}

/** Administration → Connecteurs, where the « Application Shopify » block is. */
const ADMIN_SHOPIFY_HASH = '#/admin/connectors';

/**
 * The note of a Shopify window while the office's app is not set: the token is the way; an
 * administrator gets a shortcut to the app's settings (the window closes first).
 * @param {object} ctx
 * @param {() => object|null} layerOf the window
 * @param {boolean} [noCli] the server has no Shopify CLI either
 */
function noAppNote(ctx, layerOf, noCli = false) {
  const admin = ctx.store?.get?.()?.me?.role === 'admin';
  return h('div', { class: 'stack pc-plugin-noapp' },
    alertBox('info', noCli ? `${t('pc.shopify.noApp')} ${t('pc.shopify.noCli')}` : t('pc.shopify.noApp')),
    admin ? button(t('pc.shopify.noAppAdmin'), {
      variant: 'secondary', size: 'sm', icon: 'settings', class: 'pc-plugin-noapp-go',
      onClick: () => { layerOf()?.close?.('button'); ctx.navigate?.(ADMIN_SHOPIFY_HASH); },
    }) : null);
}

/**
 * The note under the store's field: what « Se connecter avec Shopify » does here, or why it is not
 * offered (then the token is the way, and an administrator gets the shortcut to the app's settings).
 * @param {'app'|'cli'|null} via shopifyRoute()
 * @param {object|null} data normalizeLibrary()
 * @param {object} ctx
 * @param {() => object|null} layerOf the window
 */
function shopifyNoteOf(via, data, ctx, layerOf) {
  if (via === 'app') return h('p', { class: 'pc-plugin-hint muted' }, t('pc.shopify.connectHint'));
  if (via === 'cli') return h('p', { class: 'pc-plugin-hint muted' }, t('pc.shopify.connectHintCli'));
  return noAppNote(ctx, layerOf, data?.apps?.shopify?.cli === false);
}

/**
 * The fields of a plugin form, built once (a re-render keeps what is typed).
 * @param {'remote'|'mailgun'|'shopify'|'github'|'browserbase'|'vmake'} kind
 * @param {{label?: string, labelPlaceholder?: string, withLabel?: boolean, settings?: object|null, example?: string}} o
 */
function formFields(kind, { label = '', labelPlaceholder = '', withLabel = true, settings = null, example = '' }) {
  const inputs = {};
  const fields = {};
  const add = (key, control, title, opts = {}) => {
    inputs[key] = control;
    fields[key] = field(title, control, opts);
  };
  if (withLabel) {
    add('label', input({ name: 'pc-plugin-label', maxLength: LABEL_MAX, value: label, placeholder: labelPlaceholder, autocomplete: 'off' }), t('pc.add.nameLabel'), {
      hint: kind === 'shopify' ? t('pc.add.nameHintShopify') : t('pc.add.nameHint', { example: example || label || 'Canva Marie' }),
      optional: kind === 'shopify',
    });
  }
  if (kind === 'mailgun') {
    add('apiKey', input({ name: 'pc-plugin-key', type: 'password', maxLength: 200, ...NO_AUTOFILL }), t('pc.mailgun.key'), {
      hint: settings?.keyHint ? t('pc.mailgun.keyKeep', { hint: settings.keyHint }) : t('pc.mailgun.keyHint'),
    });
    add('region', select(MAILGUN_REGIONS.map((r) => [r, t(`pc.mailgun.region.${r}`)]), { name: 'pc-plugin-region', value: settings?.region === 'eu' ? 'eu' : 'us' }),
      t('pc.mailgun.region'));
    add('domain', input({ name: 'pc-plugin-domain', maxLength: 253, value: settings?.domain || '', placeholder: 'mg.exemple.com', ...NO_AUTOFILL }),
      t('pc.mailgun.domain'), { hint: t('pc.mailgun.domainHint'), optional: true });
  }
  if (kind === 'github') {
    add('apiKey', input({ name: 'pc-plugin-key', type: 'password', maxLength: 260, ...NO_AUTOFILL }), t('pc.github.key'), {
      hint: settings?.keyHint ? t('pc.github.keyKeep', { hint: settings.keyHint }) : t('pc.github.keyHint'),
    });
  }
  if (kind === 'browserbase') {
    add('apiKey', input({ name: 'pc-plugin-key', type: 'password', maxLength: 200, ...NO_AUTOFILL }), t('pc.browserbase.key'), {
      hint: settings?.keyHint ? t('pc.browserbase.keyKeep', { hint: settings.keyHint }) : t('pc.browserbase.keyHint'),
    });
  }
  if (kind === 'vmake') {
    add('accessKey', input({ name: 'pc-plugin-access-key', type: 'password', maxLength: 256, ...NO_AUTOFILL }), t('pc.vmake.accessKey'), {
      hint: settings?.keyHint ? t('pc.vmake.accessKeyKeep', { hint: settings.keyHint }) : t('pc.vmake.accessKeyHint'),
    });
    add('secretKey', input({ name: 'pc-plugin-secret-key', type: 'password', maxLength: 256, ...NO_AUTOFILL }), t('pc.vmake.secretKey'), {
      hint: settings?.keyHint ? t('pc.vmake.secretKeyKeep') : t('pc.vmake.secretKeyHint'),
    });
  }
  if (kind === 'shopify') {
    add('shop', input({ name: 'pc-plugin-shop', maxLength: 300, value: settings?.shop || '', placeholder: t('pc.shopify.shopPlaceholder'), inputmode: 'url', ...NO_AUTOFILL }),
      t('pc.shopify.shop'), { hint: t('pc.shopify.shopHint') });
    add('accessToken', input({ name: 'pc-plugin-token', type: 'password', maxLength: 210, ...NO_AUTOFILL }), t('pc.shopify.token'), { hint: t('pc.shopify.tokenHint') });
  }
  const values = () => Object.fromEntries(Object.entries(inputs).map(([k, el]) => [k, el.value]));
  const setErrors = (errors = {}) => {
    for (const k of FIELDS) fields[k]?.setError(errors[k] || null);
    const first = FIELDS.find((k) => errors[k] && inputs[k]);
    if (first) inputs[first].focus?.();
  };
  /** Keys and tokens leave their field once sent. */
  const clearSecrets = () => {
    for (const k of ['apiKey', 'accessKey', 'secretKey', 'accessToken']) if (inputs[k]) inputs[k].value = '';
  };
  /** Shopify: the store may be left empty (picked in Shopify) — its hint says so — or is required (a token). */
  const setShopOptional = (optional) => {
    const hint = fields.shop?.querySelector?.('.field-hint');
    if (hint) hint.textContent = t(optional ? 'pc.shopify.shopOptionalHint' : 'pc.shopify.shopHint');
  };
  return { inputs, fields, values, setErrors, clearSecrets, setShopOptional };
}

/** A server refusal on one field (`details.field`) goes under it; any other above the buttons. */
function showError(err, form, errorBox) {
  const f = err?.details?.field;
  if (f && form.fields[f]) {
    form.setErrors({ [f]: errorText(err) });
    errorBox.set('');
  } else {
    errorBox.set(errorText(err));
  }
}

/**
 * « Se connecter avec Shopify »: the store's authorize address from the server, then the browser
 * goes there. False (and a toast) when the server refuses or the address is not a store's.
 * @param {object} ctx
 * @param {{id: string}} conn
 * @param {{shop?: string, navigate?: (url: string) => void}} [opts]
 * @returns {Promise<boolean>}
 */
export async function startShopify(ctx, conn, { shop, navigate = ctx.navigateTo || defaultNavigate } = {}) {
  const res = await ctx.api.post(`${LIBRARY_BASE}/${enc(conn.id)}/shopify/start`, shop ? { shop } : {}, { timeoutMs: START_TIMEOUT_MS });
  const url = safeShopifyUrl(res?.url);
  if (!url) {
    ctx.toast.error(t('pc.shopify.badUrl'));
    return false;
  }
  ctx.toast.info(t('pc.shopify.redirecting'));
  navigate(url);
  return true;
}

/**
 * « Ajouter <service> »: the add window of one catalogue entry.
 * @param {object} ctx app context
 * @param {{id: string, label: string, kind: string, description?: string}} entry normalizeLibrary().catalogue item
 * @param {{data?: object|null, list?: object[], onAdded?: (c: object) => void, navigate?: (url: string) => void}} [opts]
 *   data: normalizeLibrary() (the Shopify app ready or not); list: the whole library (names taken);
 *   onAdded: the new connector (status « added » for a remote one: the caller opens its connect window)
 * @returns {import('../overlay.js').Layer}
 */
export function openCatalogAdd(ctx, entry, opts = {}) {
  // Google Drive: a name, then the caller opens its connect window (myconnectors/drive.js).
  if (entry?.id === GDRIVE_ID) return openDriveAdd(ctx, entry, opts);
  const { data = null, list = [], onAdded = () => {}, navigate } = opts;
  const kind = entry.kind === 'plugin' ? entry.id : 'remote';
  const via = kind === 'shopify' ? shopifyRoute(data) : null;
  // The owner's app lets the store be picked in Shopify (an empty field); the CLI needs it typed.
  const shopifyReady = via === 'app';
  const suggested = kind === 'shopify' ? '' : suggestLabel(entry.label, list);
  const form = formFields(kind, {
    label: suggested, labelPlaceholder: kind === 'shopify' ? t('pc.shopify.namePlaceholder') : '', example: `${entry.label} Marie`,
  });
  const errorBox = formError();
  let layer = null;
  let busy = false;
  let tokenMode = kind === 'shopify' && !via;

  const submit = button(t(kind === 'shopify' ? (via ? 'pc.shopify.connect' : 'pc.shopify.addWithToken') : 'pc.addCat.submit'), {
    variant: 'primary', type: 'submit', icon: kind === 'shopify' && via ? 'key' : 'plus',
  });
  const tokenToggle = kind === 'shopify' && via
    ? button(t('pc.shopify.useToken'), { variant: 'link', size: 'sm', onClick: () => { tokenMode = !tokenMode; render(); } }) : null;
  const shopifyNote = kind === 'shopify' ? shopifyNoteOf(via, data, ctx, () => layer) : null;
  const body = h('form', { class: 'stack pc-plugin-form', novalidate: true, onSubmit: (e) => { e?.preventDefault?.(); send(); } },
    entry.description ? h('p', { class: 'pc-plugin-desc muted' }, entry.description) : null,
    ...['label', 'apiKey', 'accessKey', 'secretKey', 'region', 'domain', 'shop'].map((k) => form.fields[k] || null),
    shopifyNote,
    form.fields.accessToken || null,
    errorBox,
    h('div', { class: 'pc-plugin-foot' }, tokenToggle, submit));

  function render() {
    if (form.fields.accessToken) form.fields.accessToken.hidden = !tokenMode;
    if (kind === 'shopify') form.setShopOptional(shopifyReady && !tokenMode);
    if (kind === 'shopify' && via) {
      const label = submit.querySelector('.btn-label');
      if (label) label.textContent = t(tokenMode ? 'pc.shopify.addWithToken' : 'pc.shopify.connect');
      const tl = tokenToggle?.querySelector('.btn-label');
      if (tl) tl.textContent = t(tokenMode ? 'pc.shopify.useOauth' : 'pc.shopify.useToken');
    }
  }

  async function send() {
    if (busy) return;
    const catalogId = entry.id;
    errorBox.set('');
    let body;
    if (kind === 'remote') {
      const v = validatePluginForm(null, form.values(), { list });
      form.setErrors(v.errors);
      if (!v.ok) return;
      body = { catalogId, label: v.value.label };
    } else {
      const v = validatePluginForm(catalogId, form.values(), { list, token: tokenMode, shopOptional: shopifyReady && !tokenMode });
      form.setErrors(v.errors);
      if (!v.ok) return;
      body = { catalogId, ...v.value, ...(via === 'cli' && !tokenMode ? { auth: 'cli' } : {}) };
    }
    busy = true;
    setBusy(submit, true);
    try {
      const saved = connectorOf(await ctx.api.post(LIBRARY_BASE, body, { timeoutMs: PLUGIN_TIMEOUT_MS }));
      form.clearSecrets();
      if (!saved) throw new Error(t('pc.gone'));
      layer?.close('done');
      if (kind === 'shopify' && !tokenMode && via === 'cli') {
        // The store is in the library (à connecter, through the CLI): the caller opens its connect window.
        onAdded(saved);
        return;
      }
      if (kind === 'shopify' && !tokenMode) {
        // The store is in the library (à connecter): now Shopify — its authorize page, or, no
        // store typed, one of its install pages where the person picks it.
        onAdded(saved);
        try {
          await startShopify(ctx, saved, { navigate });
        } catch (err) {
          ctx.toast.error(errorText(err));
        }
        return;
      }
      ctx.toast.success(t(saved.status === 'connected' ? 'pc.add.addedConnected' : 'pc.add.added', { name: saved.label }));
      onAdded(saved);
    } catch (err) {
      form.clearSecrets();
      if (layer?.isOpen?.()) showError(err, form, errorBox);
      else ctx.toast.error(errorText(err));
    } finally {
      busy = false;
      setBusy(submit, false);
    }
  }

  const cancel = button(t('common.cancel'), { variant: 'ghost', onClick: () => layer?.close('button') });
  layer = ctx.overlay.openModal({
    title: t('pc.addCat.title', { name: entry.label }), subtitle: t(`pc.addCat.subtitle.${kind}`), size: 'md',
    content: body, footer: [cancel], className: 'adm-modal pc-modal pc-plugin-modal',
  });
  layer.onClose?.(() => form.clearSecrets());
  render();
  (form.inputs.label || form.inputs.shop)?.focus?.();
  return layer;
}

/**
 * The settings window of a plugin (whoever may manage it): Mailgun — another key (empty keeps
 * the stored one), the region, the default domain (PATCH …/plugin, the key checked first);
 * Shopify — the store, « Se connecter avec Shopify » (the owner's app; or the Shopify CLI:
 * `onLogin` opens its connect window — another store is set first, PATCH {shop, auth: 'cli'},
 * while the same store keeps its current connection, a token included, until the CLI's login
 * succeeds), or a new pasted token. Reads the library first (the plugin's current settings, the
 * Shopify app ready or not, the CLI installed or not).
 * A Google Drive opens its own settings (myconnectors/drive.js: `onChanged` when its folder or the
 * storage changed).
 * @param {object} ctx app context
 * @param {{id: string, label: string, catalogId: string}} conn
 * @param {{onSaved?: (c: object) => void, onLogin?: (c: object) => void, onChanged?: () => void, navigate?: (url: string) => void,
 *   openWindow?: (url: string) => Window|null}} [opts]
 * @returns {import('../overlay.js').Layer}
 */
export function openPluginSettings(ctx, conn, opts = {}) {
  if (conn?.catalogId === GDRIVE_ID) return openDriveSettings(ctx, conn, { onChanged: opts.onChanged, navigate: opts.navigate, openWindow: opts.openWindow });
  const { onSaved = () => {}, onLogin = (c) => openConnectWindow(ctx, c), navigate } = opts;
  const kind = ['shopify', 'github', 'browserbase', 'vmake'].includes(conn.catalogId) ? conn.catalogId : 'mailgun';
  const name = conn.label || conn.id;
  let layer = null;
  let busy = false;
  let form = null;
  let via = null;
  let shopifyReady = false; // the owner's app: a store not known yet may be picked in Shopify
  let fresh = null;
  let tokenMode = false;
  let storeKnown = false; // the connector has its store already (an emptied field keeps it)
  const errorBox = formError();
  const content = h('div', { class: 'stack pc-plugin-form' }, h('p', { class: 'muted' }, t('pc.loading')));
  const save = button(t(kind === 'shopify' ? 'pc.shopify.saveToken' : 'pc.settings.save'), { variant: 'primary', icon: 'check', onClick: () => send() });
  const connect = kind === 'shopify' ? button(t('pc.shopify.connect'), { variant: 'primary', icon: 'key', onClick: () => goShopify() }) : null;
  const tokenToggle = kind === 'shopify' ? button(t('pc.shopify.useToken'), { variant: 'link', size: 'sm', onClick: () => { tokenMode = !tokenMode; render(); } }) : null;
  save.hidden = true;
  if (connect) connect.hidden = true;
  if (tokenToggle) tokenToggle.hidden = true;

  function render() {
    if (!form) return;
    if (kind === 'shopify') {
      form.fields.accessToken.hidden = !tokenMode;
      // A store already known stays when its field is emptied: the picker is for one not known yet.
      form.setShopOptional(shopifyReady && !tokenMode && !storeKnown);
      save.hidden = !tokenMode;
      connect.hidden = tokenMode || !via;
      tokenToggle.hidden = !via;
      const tl = tokenToggle.querySelector('.btn-label');
      if (tl) tl.textContent = t(tokenMode ? 'pc.shopify.useOauth' : 'pc.shopify.useToken');
    } else {
      save.hidden = false;
    }
  }

  async function load() {
    try {
      const data = normalizeLibrary(await ctx.api.get(LIBRARY_BASE));
      if (!layer?.isOpen?.()) return;
      fresh = data.connectors.find((c) => c.id === conn.id) || null;
      if (!fresh) { content.replaceChildren(alertBox('amber', t('pc.gone'))); return; }
      via = shopifyRoute(data);
      shopifyReady = via === 'app';
      tokenMode = kind === 'shopify' && !via;
      storeKnown = Boolean(fresh.plugin?.shop);
      form = formFields(kind, { withLabel: false, settings: fresh.plugin || null });
      content.replaceChildren(
        ...(kind === 'mailgun' ? [form.fields.apiKey, form.fields.region, form.fields.domain]
          : kind === 'github' || kind === 'browserbase' ? [form.fields.apiKey]
          : kind === 'vmake' ? [form.fields.accessKey, form.fields.secretKey]
          : [form.fields.shop, shopifyNoteOf(via, data, ctx, () => layer), form.fields.accessToken]),
        errorBox,
        h('form', { class: 'pc-plugin-foot', novalidate: true, onSubmit: (e) => { e?.preventDefault?.(); send(); } }, tokenToggle, connect, save));
      render();
      (form.inputs.apiKey || form.inputs.accessKey || form.inputs.shop)?.focus?.();
    } catch (err) {
      if (layer?.isOpen?.()) content.replaceChildren(alertBox('danger', errorText(err)));
    }
  }

  async function goShopify() {
    if (busy || !form) return;
    // The owner's app may pick a store not known yet in Shopify; the CLI needs one (an emptied
    // field keeps the store already known).
    const v = validatePluginForm('shopify', form.values(), { editing: true, shopOptional: via === 'app' || storeKnown });
    form.setErrors(v.errors);
    if (!v.ok) return;
    busy = true;
    setBusy(connect, true);
    try {
      if (via === 'cli') {
        // Through the Shopify CLI. The same store goes straight to its login: its token (pasted, or
        // from the owner's app) stays until the login succeeds. Another store is set first (a CLI
        // one, to connect): that drops the former connection.
        const shop = v.value.shop || fresh?.plugin?.shop;
        let target = fresh;
        if (fresh?.plugin?.shop !== shop) {
          target = connectorOf(await ctx.api.patch(`${LIBRARY_BASE}/${enc(conn.id)}/plugin`, { shop, auth: 'cli' }, { timeoutMs: PLUGIN_TIMEOUT_MS }));
        }
        if (!layer?.isOpen?.()) return;
        layer.close('done');
        onLogin(target || conn);
        return;
      }
      const ok = await startShopify(ctx, conn, { shop: v.value.shop, navigate });
      if (ok) layer?.close('done');
    } catch (err) {
      showError(err, form, errorBox);
    } finally {
      busy = false;
      setBusy(connect, false);
    }
  }

  async function send() {
    if (busy || !form) return;
    errorBox.set('');
    const v = validatePluginForm(kind, form.values(), { editing: true, token: tokenMode });
    form.setErrors(v.errors);
    if (!v.ok) return;
    busy = true;
    setBusy(save, true);
    try {
      const saved = connectorOf(await ctx.api.patch(`${LIBRARY_BASE}/${enc(conn.id)}/plugin`, v.value, { timeoutMs: PLUGIN_TIMEOUT_MS }));
      form.clearSecrets();
      if (!layer?.isOpen?.()) return;
      layer.close('done');
      if (saved) onSaved(saved);
      const after = saved || conn;
      if (after.status === 'connected') ctx.toast.success(t('pc.settings.saved', { name: after.label || name }));
      else ctx.toast.info(t(`pc.checked.${after.status}`, { name: after.label || name }));
    } catch (err) {
      form.clearSecrets();
      if (layer?.isOpen?.()) showError(err, form, errorBox);
    } finally {
      busy = false;
      setBusy(save, false);
    }
  }

  const close = button(t('common.cancel'), { variant: 'ghost', onClick: () => layer?.close('button') });
  layer = ctx.overlay.openModal({
    title: t('pc.settings.title', { name }), subtitle: t(`pc.settings.subtitle.${kind}`), size: 'md',
    content, footer: [close], className: 'adm-modal pc-modal pc-plugin-modal',
  });
  layer.onClose?.(() => form?.clearSecrets());
  load();
  return layer;
}

/**
 * The confirm window of an arrival from Shopify (`#/connectors?shopify=launch&l=<id>`: a store
 * picked in Shopify — its « Install app », the app opened from the Shopify admin, a login started
 * in another browser — with no connector chosen yet). Reads the arrival (GET …/shopify/launch/<id>):
 * « Connecter la boutique <shop> ? », the person's Shopify connectors it may go to (this store:
 * checked first, a reconnection; else the only one without a store yet, checked) and « Nouveau connecteur » (named after the
 * store once connected). CONNECTER sends the choice (POST …/shopify/launch/<id>) and the browser
 * goes to the store's authorize page; nothing is attached nor added before that click. An arrival
 * gone (expired, used, someone else's) is a toast, and so is a refusal before the window opens.
 * @param {object} ctx app context
 * @param {string} launchId
 * @param {{navigate?: (url: string) => void, onAdded?: (c: {id: string}) => void}} [opts]
 *   navigate: the browser goes to Shopify (tests pass their own); onAdded: a new connector was added
 * @returns {Promise<import('../overlay.js').Layer|null>} null when no window opened
 */
export async function openShopifyLaunch(ctx, launchId, { navigate = ctx.navigateTo || defaultNavigate, onAdded = () => {} } = {}) {
  const path = `${LIBRARY_BASE}/shopify/launch/${enc(launchId)}`;
  let launch;
  try {
    launch = shopifyLaunchOf(await ctx.api.get(path));
  } catch (err) {
    if (err?.status === 404) ctx.toast.warn(t('pc.shopify.return.launch-expired'));
    else ctx.toast.error(errorText(err));
    return null;
  }
  if (!launch) {
    ctx.toast.error(t('pc.shopify.return.launch-invalid'));
    return null;
  }
  const group = uid('pc-launch');
  // A connector already on this store first (a reconnection); else the person's only connector
  // still without a store (an earlier « Se connecter » not finished: no duplicate); else a new one.
  const waiting = launch.candidates.filter((c) => !c.shop);
  const first = launch.candidates.find((c) => c.shop === launch.shop)?.id || (waiting.length === 1 ? waiting[0].id : '');
  const radios = [];
  const option = (value, title, detail) => {
    const radio = h('input', { type: 'radio', name: group, value, checked: value === first, class: 'pc-launch-radio' });
    radios.push(radio);
    return h('label', { class: 'pc-launch-opt' }, radio,
      h('span', { class: 'pc-launch-optname' }, title),
      detail ? h('span', { class: 'pc-launch-optdetail muted' }, ` · ${detail}`) : null);
  };
  const choices = h('fieldset', { class: 'pc-launch-choices' },
    h('legend', { class: 'field-label' }, t('pc.launch.choice')),
    ...launch.candidates.map((c) => option(c.id, c.label, t(c.shop ? 'pc.launch.same' : 'pc.launch.noShop'))),
    option('', t('pc.launch.new'), t('pc.launch.newHint')));
  const errorBox = formError();
  const submit = button(t('pc.launch.submit'), { variant: 'primary', icon: 'key', type: 'submit' });
  let layer = null;
  let busy = false;

  async function send() {
    if (busy) return;
    errorBox.set('');
    const connectorId = radios.find((r) => r.checked)?.value || '';
    busy = true;
    setBusy(submit, true);
    try {
      const res = await ctx.api.post(path, connectorId ? { connectorId } : {}, { timeoutMs: START_TIMEOUT_MS });
      if (!connectorId && res?.connectorId) onAdded({ id: res.connectorId });
      const url = safeShopifyUrl(res?.url);
      if (!url) {
        ctx.toast.error(t('pc.shopify.badUrl'));
        return;
      }
      layer?.close('done');
      ctx.toast.info(t('pc.shopify.redirecting'));
      navigate(url);
    } catch (err) {
      if (err?.status === 404) {
        layer?.close('gone');
        ctx.toast.warn(t('pc.shopify.return.launch-expired'));
      } else if (layer?.isOpen?.()) {
        errorBox.set(errorText(err));
      }
    } finally {
      busy = false;
      setBusy(submit, false);
    }
  }

  const body = h('form', { class: 'stack pc-plugin-form pc-launch', novalidate: true, onSubmit: (e) => { e?.preventDefault?.(); send(); } },
    h('p', { class: 'pc-plugin-desc' }, t('pc.launch.intro')),
    choices,
    errorBox,
    h('div', { class: 'pc-plugin-foot' }, submit));
  const cancel = button(t('common.cancel'), { variant: 'ghost', onClick: () => layer?.close('button') });
  layer = ctx.overlay.openModal({
    title: t('pc.launch.title', { shop: launch.shop }), subtitle: t('pc.launch.subtitle'), size: 'md',
    content: body, footer: [cancel], className: 'adm-modal pc-modal pc-plugin-modal',
  });
  (radios.find((r) => r.checked) || radios[0])?.focus?.();
  return layer;
}
